import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-16">
      <div className="overline text-safety">Legal</div>
      <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tight text-forest leading-none">
        Privacy Policy
      </h1>
      <p className="mt-4 text-sm text-muted-foreground">Last updated: 24 August 2026</p>

      <div className="mt-10 space-y-8 text-base leading-relaxed text-foreground/90">
        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">1. Who we are</h2>
          <p>
            Furniture Exchange is a trading name of Corporate Office Furniture Ltd ("COF Ltd",
            "we", "us", "our"), a company registered in England and Wales. COF Ltd is the data
            controller for personal data collected through this website, for the purposes of the
            UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">2. Information we collect</h2>
          <p>We collect the following categories of personal data:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Account data: name, email address, phone number, password (hashed), business/company name.</li>
            <li>Listing data: descriptions, photos, pricing and location of items you list.</li>
            <li>Transaction data: order details, invoices and payment confirmations (card details are handled directly by Stripe and are not stored on our servers).</li>
            <li>Communications: messages sent through our inbox/messaging features and correspondence with support.</li>
            <li>Technical data: IP address, browser type, device information and usage data collected via cookies (see our Cookie Policy).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">3. How we use your information</h2>
          <p>We use your personal data to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Create and manage your account and provide the marketplace service (performance of a contract).</li>
            <li>Process payments and prevent fraud (contract and legitimate interests).</li>
            <li>Verify sellers and removal companies (legitimate interests).</li>
            <li>Respond to enquiries and provide customer support (contract and legitimate interests).</li>
            <li>Send service-related communications, such as order confirmations (contract and legal obligation).</li>
            <li>With your consent, send you marketing communications about Furniture Exchange and, where you have agreed, about relevant products and services offered by our parent company, Corporate Office Furniture Ltd (consent).</li>
            <li>Comply with our legal and regulatory obligations, including tax and accounting records (legal obligation).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">4. Marketing and your choices</h2>
          <p>
            Because Furniture Exchange is owned and operated by Corporate Office Furniture Ltd, if
            you opt in to marketing at registration or elsewhere on the Platform, we may contact
            you from time to time about office furniture and related offers from Corporate Office
            Furniture Ltd, as well as updates about Furniture Exchange. We will never sell your
            personal data to unrelated third parties for their own marketing purposes. You can
            withdraw marketing consent at any time by using the unsubscribe link in any marketing
            email, updating your account preferences, or emailing{" "}
            <a href="mailto:info@furnitureexchange.co.uk" className="text-safety hover:underline">
              info@furnitureexchange.co.uk
            </a>
            . Withdrawing consent will not affect service communications necessary to operate your
            account.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">5. Legal basis for processing</h2>
          <p>
            We rely on the following legal bases under UK GDPR Article 6: performance of a
            contract with you, compliance with a legal obligation, our legitimate interests (for
            example, fraud prevention and platform security) balanced against your rights, and your
            consent (for marketing and non-essential cookies). Where we rely on legitimate
            interests, you have the right to object.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">6. Sharing your information</h2>
          <p>We may share personal data with:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Stripe, for payment processing.</li>
            <li>Other users, where necessary to complete a transaction (for example, a buyer's contact details may be shared with a seller).</li>
            <li>Corporate Office Furniture Ltd, our parent company, for the marketing purposes described above where you have consented, and for group administration.</li>
            <li>IT and hosting service providers who process data on our behalf under contract.</li>
            <li>Regulators, law enforcement or courts where required by law.</li>
          </ul>
          <p className="mt-2">We do not sell your personal data to third parties.</p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">7. International transfers</h2>
          <p>
            Some of our service providers may process data outside the UK. Where this occurs, we
            ensure appropriate safeguards are in place, such as the UK's International Data
            Transfer Addendum or adequacy regulations, in accordance with UK GDPR.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">8. Data retention</h2>
          <p>
            We retain personal data for as long as necessary to provide the service and to comply
            with our legal obligations, for example retaining transaction records for tax purposes
            for six years. Account data is generally retained for the life of your account plus a
            reasonable period afterwards, unless you ask us to delete it sooner and we have no
            legal obligation to retain it.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">9. Your rights</h2>
          <p>Under UK GDPR, you have the right to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate data.</li>
            <li>Request erasure of your data, in certain circumstances.</li>
            <li>Object to or restrict certain processing.</li>
            <li>Request data portability.</li>
            <li>Withdraw consent at any time where processing is based on consent.</li>
            <li>Lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk.</li>
          </ul>
          <p className="mt-2">
            To exercise any of these rights, email{" "}
            <a href="mailto:info@furnitureexchange.co.uk" className="text-safety hover:underline">
              info@furnitureexchange.co.uk
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">10. Security</h2>
          <p>
            We use appropriate technical and organisational measures to protect your personal
            data, including encryption in transit, access controls, and secure hosting.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">11. Cookies</h2>
          <p>
            We use cookies and similar technologies as described in our{" "}
            <Link to="/cookies" className="text-safety hover:underline">Cookie Policy</Link>.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">12. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Material changes will be notified
            via the Platform or by email where appropriate.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">13. Contact us</h2>
          <p>
            Corporate Office Furniture Ltd, trading as Furniture Exchange. Email:{" "}
            <a href="mailto:info@furnitureexchange.co.uk" className="text-safety hover:underline">
              info@furnitureexchange.co.uk
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
